import axios from 'axios';
import { subYears } from 'date-fns';

import { useOktaAuth } from '@okta/okta-react';
import { getConfig } from '@tcs/client-portal/config/configService';
import { useMutation, useQueryClient } from 'react-query';
import { ReportTimeframe } from '@tcs/client-portal-api/types/ReportType';
export interface GenerateReportRequest {
  clientNames?: string[];
  accounts?: string[];
  reportType: string;
  timeframe: ReportTimeframe | string;
  fromDate: Date | number;
  toDate: Date | number;
  reportName?: string;
}

const REPORT_TYPE_MAP: Record<string, string> = {
  accounts: 'Accounts Report',
  positions: 'Positions Report',
  transactions: 'Transaction Report',
  beneficiaries: 'Beneficiary Report',
};

const getDateRange = (
  timeframe: ReportTimeframe,
  fromDate?: Date | number,
  toDate?: Date | number
): {
  startDate?: string;
  endDate?: string;
} => {
  const today = new Date();
  const lastYear = subYears(today, 1);

  const pad = (n: number) => n.toString().padStart(2, '0');

  let startDate: string | undefined;
  let endDate: string | undefined;

  switch (timeframe) {
    case ReportTimeframe.LAST_YEAR:
      startDate = lastYear.getFullYear() + '-' + pad(1) + '-' + pad(1);
      endDate = lastYear.getFullYear() + '-' + pad(12) + '-' + pad(31);
      break;

    case ReportTimeframe.CUSTOM_TIME_RANGE:
      const from = fromDate as Date;
      const to = toDate as Date;

      startDate =
        from.getFullYear() +
        '-' +
        pad(from.getMonth() + 1) +
        '-' +
        pad(from.getDate());

      endDate =
        to.getFullYear() +
        '-' +
        pad(to.getMonth() + 1) +
        '-' +
        pad(to.getDate());
      break;

    case ReportTimeframe.YEAR_TO_DATE:
      startDate = today.getFullYear() + '-' + pad(1) + '-' + pad(1);

      endDate =
        today.getFullYear() +
        '-' +
        pad(today.getMonth() + 1) +
        '-' +
        pad(today.getDate());
      break;

    default:
      startDate = undefined;
      endDate = undefined;
  }

  return { startDate, endDate };
};

export const useGenerateReportMutation = (representativeCode: string) => {
  const queryClient = useQueryClient();
  const { oktaAuth } = useOktaAuth();

  return useMutation(
    async (formData: GenerateReportRequest) => {
      const config = getConfig();
      const tokens = await oktaAuth.tokenManager.getTokens();
      const token = tokens.accessToken?.accessToken;

      const { startDate, endDate } = getDateRange(
        formData.timeframe as ReportTimeframe,
        formData.fromDate,
        formData.toDate
      );

      const payload = {
        reportType: REPORT_TYPE_MAP[formData.reportType] || formData.reportType,
        ...(!!startDate && { startDate: startDate }),
        ...(!!endDate && { endDate: endDate }),
        reportName: formData.reportName,
        clientNames: formData.clientNames,
        accounts: formData.accounts,
      };

      const url = `${config.API_BASE_URL}/rss/generatedreports/adhoc/${representativeCode}`;

      return axios.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'x-tcs-authorization': token,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          `/rss/generatedreports/${representativeCode}`
        );
      },
    }
  );
};

export default useGenerateReportMutation;
